import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Wind, Droplets, Sun, Search, Loader2, Gauge, MapPin, AlertTriangle, Pencil, Plus, X, Check, Plane, Globe2 } from "lucide-react";
import { Link } from "react-router-dom";
import { MetarPanel } from "@/components/MetarPanel";

const DEFAULT_QUICK = ["Hervey Bay", "Bundaberg", "Maryborough", "Moranbah", "Kingaroy", "Brisbane"];
const MAX_SAFE_WIND = 20; // knots, typical drone limit

const windOk = (knots) => knots == null ? null : knots <= MAX_SAFE_WIND;

export default function WeatherPage() {
  const [q, setQ] = useState("");
  const [input, setInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quick, setQuick] = useState(DEFAULT_QUICK);
  const [managing, setManaging] = useState(false);
  const [newLoc, setNewLoc] = useState("");
  const [icao, setIcao] = useState("");
  const [apQuery, setApQuery] = useState("");
  const [apSuggest, setApSuggest] = useState([]);
  const [apOpen, setApOpen] = useState(false);
  const [apCoords, setApCoords] = useState(null);
  const [favAero, setFavAero] = useState([]);
  const [managingAero, setManagingAero] = useState(false);
  const [newAeroCode, setNewAeroCode] = useState("");
  const [newAeroName, setNewAeroName] = useState("");
  const [nearestAero, setNearestAero] = useState([]);
  const [searchLabel, setSearchLabel] = useState("");

  const selectNearestAero = (a) => {
    setIcao(a.id);
    setApCoords({ lat: a.lat, lon: a.lng });
    setApQuery(`${a.id} — ${a.name}`);
    setApOpen(false);
  };

  useEffect(() => {
    api.get("/settings/favourite-aerodromes")
      .then((r) => { if (Array.isArray(r.data.aerodromes)) setFavAero(r.data.aerodromes); })
      .catch(() => {});
  }, []);

  const persistFavAero = async (list) => {
    setFavAero(list);
    try { await api.put("/settings/favourite-aerodromes", { aerodromes: list }); }
    catch { toast.error("Could not save favourite aerodromes"); }
  };
  const addFavAero = () => {
    const code = newAeroCode.trim().toUpperCase();
    if (!code) return;
    if (favAero.some((a) => a.code === code)) { setNewAeroCode(""); setNewAeroName(""); return; }
    persistFavAero([...favAero, { code, name: newAeroName.trim() }]);
    setNewAeroCode(""); setNewAeroName("");
  };
  const removeFavAero = (code) => persistFavAero(favAero.filter((a) => a.code !== code));
  const openFavAero = (a) => { setIcao(a.code); setApCoords(null); setApQuery(a.name ? `${a.code} — ${a.name}` : a.code); setApOpen(false); };

  useEffect(() => {
    const t = setTimeout(async () => {
      const s = apQuery.trim();
      if (s.length < 2 || s.includes("—")) { setApSuggest([]); return; }
      try {
        const r = await api.get("/metar/airports/search", { params: { q: s } });
        setApSuggest(r.data); setApOpen(true);
      } catch { setApSuggest([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [apQuery]);

  const selectAerodrome = (a) => {
    setIcao((a.id || "").toUpperCase().trim());
    setApCoords(a.lat != null && a.lng != null ? { lat: a.lat, lon: a.lng } : null);
    setApQuery(`${a.id} — ${a.name}`);
    setApOpen(false); setApSuggest([]);
  };
  const submitAerodrome = (e) => {
    e.preventDefault();
    const code = apQuery.trim().split("—")[0].trim().toUpperCase();
    if (code) { setIcao(code); setApCoords(null); setApOpen(false); }
  };
  const clearAerodrome = () => { setIcao(""); setApQuery(""); setApSuggest([]); setApCoords(null); if (q) load({ query: q }); };

  useEffect(() => {
    api.get("/settings/weather-locations")
      .then((r) => { if (Array.isArray(r.data.locations) && r.data.locations.length) setQuick(r.data.locations); })
      .catch(() => {});
  }, []);

  const persistQuick = async (list) => {
    setQuick(list);
    try { await api.put("/settings/weather-locations", { locations: list }); }
    catch { toast.error("Could not save quick locations"); }
  };
  const addQuick = () => {
    const v = newLoc.trim();
    if (!v) return;
    if (quick.some((c) => c.toLowerCase() === v.toLowerCase())) { setNewLoc(""); return; }
    persistQuick([...quick, v]); setNewLoc("");
  };
  const removeQuick = (c) => persistQuick(quick.filter((x) => x !== c));

  const load = useCallback(async ({ query, lat, lon, label }) => {
    setLoading(true);
    setNearestAero([]);
    setSearchLabel(label || query || "");
    try {
      const byCoords = lat != null && lon != null;
      const params = byCoords ? { lat, lon, q: label || query || "" } : { q: query };
      const res = await api.get("/weather", { params });
      setData(res.data);
      // Find nearest 5 airfields to the searched location
      let glat = byCoords ? lat : null;
      let glon = byCoords ? lon : null;
      if (glat == null && query) {
        try {
          const g = await api.get("/geocode", { params: { q: query } });
          if (g.data?.found) { glat = g.data.lat; glon = g.data.lon; }
        } catch { /* best-effort */ }
      }
      if (glat != null && glon != null) {
        try {
          const na = await api.get("/metar/airports/nearest", { params: { lat: glat, lon: glon, n: 5 } });
          setNearestAero(na.data || []);
        } catch { /* nearest airfields are best-effort */ }
      }
    } catch (e) {
      toast.error("Could not load weather for that location");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (q) load({ query: q }); else { setData(null); setNearestAero([]); setSearchLabel(""); } }, [q, load]);

  // When an aerodrome is selected/searched, also load BOM/open-meteo weather + rain radar + Windy for its location.
  useEffect(() => {
    if (!icao) return;
    let cancelled = false;
    (async () => {
      let coords = apCoords;
      if (!coords) {
        try {
          const r = await api.get("/metar/airports/search", { params: { q: icao } });
          const hit = (r.data || []).find((a) => (a.id || "").toUpperCase() === icao.toUpperCase());
          if (hit && hit.lat != null && hit.lng != null) coords = { lat: hit.lat, lon: hit.lng };
        } catch { /* fall through — METAR panel still works by ICAO */ }
      }
      if (!cancelled && coords) {
        setApCoords(coords);
        load({ lat: coords.lat, lon: coords.lon, label: apQuery || icao });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [icao]);

  const submit = (e) => { e.preventDefault(); if (input.trim()) setQ(input.trim()); };
  const clearMain = () => { setInput(""); setQ(""); setData(null); setNearestAero([]); setSearchLabel(""); };

  const obs = data?.observations;
  const today = data?.daily?.[0];
  const hourly = data?.hourly || [];
  const currentWindKnot = obs?.wind?.speed_knot ?? hourly?.[0]?.wind?.speed_knot;
  const flyable = windOk(currentWindKnot);

  const geohash = data?.location?.geohash;

  return (
    <div className="space-y-6 fade-up">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-600 mb-1">Flight Weather · Live BOM Data</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-heading">Weather</h1>
          <p className="text-slate-500 mt-1">Live observations & forecasts from the Bureau of Meteorology, plus Windy wind maps.</p>
        </div>
        <Link to="/earth" data-testid="weather-earth-data-btn" className="shrink-0 inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium px-4 py-2 transition-colors">
          <Globe2 className="h-4 w-4 text-lime-400" /> Earth Data
        </Link>
      </div>

      <form onSubmit={submit} className="flex gap-2 max-w-lg">
        <div className="relative flex-1">
          <Input data-testid="weather-search-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Search town or job location…" className="pr-9" />
          {input && (
            <button type="button" onClick={clearMain} data-testid="weather-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
          )}
        </div>
        <Button data-testid="weather-search-btn" type="submit" className="bg-slate-900 hover:bg-slate-800 text-white">
          <Search className="h-4 w-4" />
        </Button>
      </form>
      <div className="flex flex-wrap items-center gap-2">
        {quick.map((c) => (
          <span key={c} className="relative inline-flex">
            <button onClick={() => { setInput(c); setQ(c); }} data-testid={`weather-quick-${c}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${q === c ? "bg-lime-400 border-lime-400 text-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"} ${managing ? "pr-7" : ""}`}>
              {c}
            </button>
            {managing && (
              <button onClick={() => removeQuick(c)} data-testid={`weather-quick-remove-${c}`}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600">
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        {managing ? (
          <div className="inline-flex items-center gap-1">
            <Input data-testid="weather-quick-add-input" value={newLoc} onChange={(e) => setNewLoc(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addQuick()} placeholder="Add location…" className="h-9 w-40" />
            <Button size="icon" onClick={addQuick} data-testid="weather-quick-add-btn" className="h-9 w-9 bg-slate-900 hover:bg-slate-800 text-white"><Plus className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" onClick={() => setManaging(false)} data-testid="weather-quick-done-btn"><Check className="h-4 w-4 mr-1" /> Done</Button>
          </div>
        ) : (
          <button onClick={() => setManaging(true)} data-testid="weather-quick-edit-btn"
            className="px-2.5 py-1.5 rounded-full text-sm font-medium border border-dashed border-slate-300 text-slate-500 hover:border-slate-400 inline-flex items-center gap-1">
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        )}
      </div>

      {/* Aerodrome (ICAO / name) search */}
      <div className="relative max-w-lg">
        <form onSubmit={submitAerodrome} className="flex gap-2">
          <div className="relative flex-1">
            <Plane className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input className="pl-9 pr-9" data-testid="aerodrome-search-input" value={apQuery}
              onChange={(e) => setApQuery(e.target.value)} onFocus={() => apSuggest.length && setApOpen(true)}
              placeholder="Aerodrome by ICAO (e.g. YMND) or name…" />
            {apQuery && (
              <button type="button" onClick={clearAerodrome} data-testid="aerodrome-search-clear"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
            )}
          </div>
          <Button type="submit" data-testid="aerodrome-search-btn" className="bg-lime-500 hover:bg-lime-400 text-slate-900">
            <Search className="h-4 w-4" />
          </Button>
        </form>
        {apOpen && apSuggest.length > 0 && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden max-h-72 overflow-y-auto" data-testid="aerodrome-suggestions">
            {apSuggest.map((a) => (
              <button key={a.id} type="button" data-testid={`aerodrome-opt-${a.id}`} onClick={() => selectAerodrome(a)}
                className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-2">
                <span className="text-sm text-slate-800 truncate"><span className="font-mono font-semibold text-lime-700">{a.id}</span> — {a.name}</span>
                <span className="text-[10px] text-slate-400 shrink-0">{[a.metar && "METAR", a.taf && "TAF"].filter(Boolean).join(" · ")}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Favourite aerodromes */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-400 inline-flex items-center gap-1"><Plane className="h-3.5 w-3.5" /> Favourites</span>
        {favAero.map((a) => (
          <span key={a.code} className="relative inline-flex">
            <button onClick={() => openFavAero(a)} data-testid={`fav-aero-${a.code}`}
              className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${icao === a.code ? "bg-lime-400 border-lime-400 text-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"} ${managingAero ? "pr-7" : ""}`}>
              <span className="font-mono font-semibold">{a.code}</span>{a.name ? ` · ${a.name}` : ""}
            </button>
            {managingAero && (
              <button onClick={() => removeFavAero(a.code)} data-testid={`fav-aero-remove-${a.code}`}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600">
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
        {managingAero ? (
          <div className="inline-flex items-center gap-1">
            <Input data-testid="fav-aero-add-code" value={newAeroCode} onChange={(e) => setNewAeroCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addFavAero()} placeholder="ICAO (YSBK)" className="h-9 w-28" />
            <Input data-testid="fav-aero-add-name" value={newAeroName} onChange={(e) => setNewAeroName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addFavAero()} placeholder="Label (optional)" className="h-9 w-36" />
            <Button size="icon" onClick={addFavAero} data-testid="fav-aero-add-btn" className="h-9 w-9 bg-slate-900 hover:bg-slate-800 text-white"><Plus className="h-4 w-4" /></Button>
            <Button size="sm" variant="outline" onClick={() => setManagingAero(false)} data-testid="fav-aero-done-btn"><Check className="h-4 w-4 mr-1" /> Done</Button>
          </div>
        ) : (
          <button onClick={() => setManagingAero(true)} data-testid="fav-aero-edit-btn"
            className="px-2.5 py-1.5 rounded-full text-sm font-medium border border-dashed border-slate-300 text-slate-500 hover:border-slate-400 inline-flex items-center gap-1">
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        )}
      </div>

      {/* Nearest airfields to searched town */}
      {nearestAero.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="nearest-airfields">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2 inline-flex items-center gap-1">
            <Plane className="h-3.5 w-3.5" /> Nearest airfields to “{searchLabel}” — select to view below
          </p>
          <div className="flex flex-wrap gap-2">
            {nearestAero.map((a) => (
              <button key={a.id} onClick={() => selectNearestAero(a)} data-testid={`nearest-aero-${a.id}`}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm border transition-all ${icao === a.id ? "bg-lime-400 border-lime-400 text-slate-900" : "bg-slate-50 border-slate-200 text-slate-700 hover:border-slate-300"}`}>
                <span className="font-mono font-semibold text-lime-700">{a.id}</span>
                <span className="truncate max-w-[160px]">{a.name}</span>
                {a.metar && <span className="text-[9px] font-bold uppercase tracking-wide px-1 py-0.5 rounded bg-lime-100 text-lime-700">METAR</span>}
                <span className="text-[11px] text-slate-400">{a.distance_km} km</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {icao && (
        <div data-testid="aerodrome-metar">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold font-heading text-slate-900">Aerodrome weather — <span className="font-mono text-lime-700">{icao}</span></h2>
            <button onClick={clearAerodrome} data-testid="aerodrome-clear" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"><X className="h-4 w-4" /> Clear</button>
          </div>
          <MetarPanel icao={icao} lat={apCoords?.lat} lon={apCoords?.lon} title={`METAR / TAF — ${icao}`} />
          <p className="text-xs text-slate-400 mt-2">Includes pressure &amp; density altitude at field elevation. Source: metar-taf.com.</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-lime-500" /></div>
      ) : !data ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center text-slate-500" data-testid="weather-empty">
          Search a town or job location above for live BOM weather, or search an aerodrome for METAR / TAF.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 text-slate-700">
            <MapPin className="h-4 w-4 text-lime-600" />
            <span className="font-heading font-semibold">{data.location.name}, {data.location.state}</span>
          </div>

          {/* Flyability banner */}
          {flyable !== null && (
            <div data-testid="flyability-banner" className={`rounded-xl p-4 border flex items-center gap-3 ${flyable ? "bg-lime-50 border-lime-300" : "bg-red-50 border-red-300"}`}>
              {flyable ? <Wind className="h-6 w-6 text-lime-600" /> : <AlertTriangle className="h-6 w-6 text-red-600" />}
              <div>
                <p className={`font-heading font-semibold ${flyable ? "text-lime-800" : "text-red-800"}`}>
                  {flyable ? "Conditions look flyable" : "Caution — wind exceeds typical limit"}
                </p>
                <p className="text-sm text-slate-600">Current wind {currentWindKnot ?? "–"} kn (advisory limit {MAX_SAFE_WIND} kn). Always verify against your platform's rated limits.</p>
              </div>
            </div>
          )}

          {/* Current conditions */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile icon={Gauge} label="Temperature" value={obs?.temp != null ? `${obs.temp}°C` : today?.temp_max != null ? `${today.temp_max}°C max` : "–"} sub={obs?.temp_feels_like != null ? `Feels ${obs.temp_feels_like}°C` : ""} accent="bg-orange-50 text-orange-600" />
            <StatTile icon={Wind} label="Wind" value={currentWindKnot != null ? `${currentWindKnot} kn` : "–"} sub={obs?.wind?.direction ? `${obs.wind.direction}${obs?.gust?.speed_knot ? ` · gust ${obs.gust.speed_knot} kn` : ""}` : (hourly?.[0]?.wind?.direction || "")} accent="bg-sky-50 text-sky-600" />
            <StatTile icon={Droplets} label="Rain chance" value={today?.rain?.chance != null ? `${today.rain.chance}%` : "–"} sub={today?.rain?.amount?.max ? `up to ${today.rain.amount.max}mm` : "today"} accent="bg-blue-50 text-blue-600" />
            <StatTile icon={Sun} label="UV Index" value={today?.uv?.max_index != null ? `${today.uv.max_index}` : "–"} sub={today?.uv?.category || ""} accent="bg-amber-50 text-amber-600" />
          </div>

          {today?.short_text && (
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <p className="text-sm text-slate-700">{today.extended_text || today.short_text}</p>
              {today.fire_danger_category?.text && <p className="text-xs text-slate-500 mt-2">Fire danger: <span className="font-medium">{today.fire_danger_category.text}</span></p>}
            </div>
          )}

          {/* Hourly wind strip */}
          {hourly.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold font-heading text-slate-900 mb-3">Next hours — wind & rain</h2>
              <div className="flex gap-3 overflow-x-auto pb-2" data-testid="hourly-strip">
                {hourly.map((h, i) => {
                  const kn = h.wind?.speed_knot;
                  const ok = windOk(kn);
                  return (
                    <div key={h.time || i} className="shrink-0 w-24 bg-white border border-slate-200 rounded-xl p-3 text-center">
                      <p className="text-xs text-slate-400">{new Date(h.time).toLocaleTimeString("en-AU", { hour: "numeric", timeZone: "Australia/Brisbane" })}</p>
                      <p className="text-lg font-semibold text-slate-900 mt-1">{h.temp}°</p>
                      <p className={`text-sm font-medium mt-1 ${ok === false ? "text-red-600" : "text-slate-700"}`}>{kn ?? "–"} kn</p>
                      <p className="text-[11px] text-slate-400">{h.wind?.direction}</p>
                      <p className="text-[11px] text-blue-500 mt-0.5">{h.rain?.chance ?? 0}%</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 7-day forecast */}
          {data.daily?.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold font-heading text-slate-900 mb-3">7-day forecast</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {data.daily.slice(0, 7).map((d) => (
                  <div key={d.date} className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                    <p className="text-xs font-bold uppercase text-slate-500">{new Date(d.date).toLocaleDateString("en-AU", { weekday: "short", timeZone: "Australia/Brisbane" })}</p>
                    <p className="text-xl font-bold text-slate-900 mt-2">{d.temp_max ?? "–"}°</p>
                    <p className="text-xs text-slate-400">{d.temp_min ?? "–"}°</p>
                    <p className="text-[11px] text-slate-500 mt-1 line-clamp-2 min-h-[28px]">{d.short_text}</p>
                    <p className="text-[11px] text-blue-500 mt-1">{d.rain?.chance ?? 0}% rain</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Aviation weather — METAR / TAF (nearest airport) */}
          {geohash && (() => {
            const c = decodeGeohash(geohash);
            return c ? (
              <div data-testid="weather-metar">
                <h2 className="text-lg font-semibold font-heading text-slate-900 mb-3">Aviation weather — nearest aerodrome</h2>
                <MetarPanel lat={c.lat} lon={c.lon} title="METAR / TAF" />
                <p className="text-xs text-slate-400 mt-2">METAR/TAF for the closest reporting aerodrome. Source: metar-taf.com.</p>
              </div>
            ) : null;
          })()}

          {/* Rain radar */}
          {geohash && (
            <div>
              <h2 className="text-lg font-semibold font-heading text-slate-900 mb-3">Rain radar</h2>
              <div className="rounded-xl overflow-hidden border border-slate-200 bg-white" data-testid="rain-radar">
                <RadarMap geohash={geohash} name={data.location.name} />
              </div>
              <p className="text-xs text-slate-400 mt-2">Radar recenters to your search. Use the map's own search &amp; layer menu to explore. Radar © Bureau of Meteorology (via Windy.com).</p>
            </div>
          )}

          {/* Windy embed map */}
          {geohash && (
            <div>
              <h2 className="text-lg font-semibold font-heading text-slate-900 mb-3">Windy — live wind map</h2>
              <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">
                <WindyMap name={data.location.name} />
              </div>
              <p className="text-xs text-slate-400 mt-2">Wind visualisation by Windy.com. BOM data © Bureau of Meteorology.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const StatTile = ({ icon: Icon, label, value, sub, accent }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5">
    <span className={`h-10 w-10 rounded-lg flex items-center justify-center ${accent}`}><Icon className="h-5 w-5" /></span>
    <p className="mt-3 text-2xl font-bold font-heading text-slate-900">{value}</p>
    <p className="text-sm text-slate-500">{label}</p>
    {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

function WindyMap({ name }) {
  // Windy free embed (no API key). Uses location name search via lat/lon-less overview centred on QLD.
  const src = `https://embed.windy.com/embed2.html?zoom=6&level=surface&overlay=wind&menu=&message=true&marker=&calendar=&pressure=&type=map&location=coordinates&detail=&detailLat=-25.5&detailLon=152.8&metricWind=kt&metricTemp=%C2%B0C&radarRange=-1`;
  return <iframe title={`Windy wind map ${name}`} src={src} className="w-full h-[840px]" frameBorder="0" />;
}

function decodeGeohash(gh) {
  const BASE32 = "0123456789bcdefghjkmnpqrstuvwxyz";
  let evenBit = true;
  let latMin = -90, latMax = 90, lonMin = -180, lonMax = 180;
  for (let i = 0; i < gh.length; i++) {
    const idx = BASE32.indexOf(gh[i].toLowerCase());
    if (idx === -1) return null;
    for (let n = 4; n >= 0; n--) {
      const bit = (idx >> n) & 1;
      if (evenBit) {
        const mid = (lonMin + lonMax) / 2;
        if (bit === 1) lonMin = mid; else lonMax = mid;
      } else {
        const mid = (latMin + latMax) / 2;
        if (bit === 1) latMin = mid; else latMax = mid;
      }
      evenBit = !evenBit;
    }
  }
  return { lat: (latMin + latMax) / 2, lon: (lonMin + lonMax) / 2 };
}

function RadarMap({ geohash, name }) {
  const c = decodeGeohash(geohash) || { lat: -25.5, lon: 152.8 };
  const lat = c.lat.toFixed(3);
  const lon = c.lon.toFixed(3);
  const src = `https://embed.windy.com/embed2.html?lat=${lat}&lon=${lon}&zoom=8&level=surface&overlay=radar&product=radar&menu=true&message=true&marker=true&calendar=&pressure=&type=map&location=coordinates&detail=&detailLat=${lat}&detailLon=${lon}&metricWind=kt&metricTemp=%C2%B0C&radarRange=-1`;
  return <iframe key={`${lat},${lon}`} title={`Rain radar ${name}`} src={src} className="w-full h-[560px]" frameBorder="0" />;
}
